import React, { useState } from 'react'
import { Plus, Trash2, MessageSquare } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Conversation } from '../types'

interface ConversationListProps {
  conversations: Conversation[]
  activeId: string | null
  onSelect: (id: string) => void
  onCreate: () => void
  onDelete: (id: string) => void
  loading?: boolean
  mobileOpen?: boolean
  onCloseMobile?: () => void
}

interface DeleteConfirmProps {
  title: string
  onConfirm: () => void
  onCancel: () => void
}

function DeleteConfirm({ title, onConfirm, onCancel }: DeleteConfirmProps) {
  const { currentTheme } = useTheme()
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
      onClick={onCancel}
    >
      <div
        className="w-80 rounded-2xl p-5 shadow-xl"
        style={{ backgroundColor: currentTheme.secondary, border: `1px solid ${currentTheme.border}` }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(239,68,68,0.12)' }}>
            <Trash2 className="w-4 h-4" style={{ color: '#EF4444' }} />
          </div>
          <div>
            <p className="text-sm font-semibold mb-1" style={{ color: currentTheme.text }}>Delete conversation?</p>
            <p className="text-xs leading-relaxed" style={{ color: currentTheme.mutedText }}>
              "<span style={{ color: currentTheme.secondrytext }}>{title}</span>" will be permanently deleted.
            </p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-150"
            style={{ backgroundColor: currentTheme.hoverBackground, color: currentTheme.secondrytext }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = currentTheme.activeBackground }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = currentTheme.hoverBackground }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-150"
            style={{ backgroundColor: '#EF4444', color: '#fff' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#DC2626' }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#EF4444' }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

function relativeTime(date: Date): string {
  const diff = Date.now() - date.getTime()
  const mins = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  if (hours < 24) return `${hours}h`
  if (days < 7) return `${days}d`
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeId,
  onSelect,
  onCreate,
  onDelete,
  loading = false,
  mobileOpen = false,
  onCloseMobile,
}) => {
  const { currentTheme } = useTheme()
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const confirmTarget = confirmDeleteId ? conversations.find(c => c.id === confirmDeleteId) : null

  return (
    <>
      {confirmTarget && (
        <DeleteConfirm
          title={confirmTarget.title}
          onConfirm={() => { onDelete(confirmTarget.id); setConfirmDeleteId(null) }}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <div
        className={`fixed md:relative inset-y-0 left-0 z-50 flex flex-col w-80 shrink-0 h-full border-r transition-transform duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        style={{ backgroundColor: currentTheme.secondary, borderColor: currentTheme.border }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between h-14 px-5 border-b shrink-0"
          style={{ backgroundColor: currentTheme.secondary, borderColor: currentTheme.border }}
        >
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: currentTheme.mutedText }}>
            Conversations
          </span>
          <button
            onClick={() => { onCreate(); onCloseMobile?.() }}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150"
            style={{ backgroundColor: currentTheme.accent, color: '#FFFFFF' }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.85' }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
            title="New conversation"
            aria-label="New conversation"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* List */}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar py-2 px-2.5">
          {loading ? (
            <div className="space-y-1.5 px-1 pt-1">
              {[80, 60, 72, 50].map(w => (
                <div key={w} className="px-3.5 py-3.5 rounded-xl" style={{ backgroundColor: currentTheme.hoverBackground }}>
                  <div className="h-3 rounded-full mb-2.5" style={{ width: `${w}%`, backgroundColor: currentTheme.border }} />
                  <div className="h-2.5 rounded-full" style={{ width: '45%', backgroundColor: currentTheme.borderSecondary }} />
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 gap-2">
              <MessageSquare className="w-7 h-7" style={{ color: currentTheme.mutedText, opacity: 0.3 }} />
              <p className="text-xs" style={{ color: currentTheme.mutedText }}>No conversations yet</p>
            </div>
          ) : (
            conversations.map(conv => {
              const isActive = conv.id === activeId
              const isHovered = conv.id === hoveredId

              return (
                <div
                  key={conv.id}
                  className="relative mb-0.5 rounded-xl cursor-pointer transition-colors duration-150"
                  style={{
                    backgroundColor: isActive ? currentTheme.activeBackground : isHovered ? currentTheme.hoverBackground : 'transparent',
                    border: `1px solid ${isActive ? currentTheme.border : 'transparent'}`,
                  }}
                  onClick={() => { onSelect(conv.id); onCloseMobile?.() }}
                  onMouseEnter={() => setHoveredId(conv.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  {isActive && (
                    <span
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r-full"
                      style={{ backgroundColor: currentTheme.accent }}
                    />
                  )}

                  <div className="px-3.5 py-3.5 pr-9">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p
                        className="text-sm font-medium truncate leading-snug"
                        style={{ color: isActive ? currentTheme.text : currentTheme.secondrytext }}
                      >
                        {conv.title}
                      </p>
                      <span className="text-[11px] shrink-0" style={{ color: currentTheme.mutedText }}>
                        {relativeTime(conv.timestamp)}
                      </span>
                    </div>
                    {conv.lastMessage && (
                      <p className="text-xs truncate" style={{ color: currentTheme.mutedText }}>
                        {conv.lastMessage}
                      </p>
                    )}
                  </div>

                  {isHovered && (
                    <button
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg flex items-center justify-center transition-colors duration-150"
                      style={{ backgroundColor: currentTheme.hoverBackground, color: currentTheme.mutedText }}
                      onClick={e => { e.stopPropagation(); setConfirmDeleteId(conv.id) }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.10)' }}
                      onMouseLeave={e => { e.currentTarget.style.color = currentTheme.mutedText; e.currentTarget.style.backgroundColor = currentTheme.hoverBackground }}
                      aria-label="Delete conversation"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </>
  )
}
